<script setup lang="ts">
import type {
	GroupItemClickEvent,
	Suggestion
} from '~/components/SearchSuggest/types';
import type { SelectAddressEvent } from '~/composables/useNewSearchSuggest';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { FilterApplied } from '~/types/mixpanel';

import { useResearchsSuggest } from '~/composables/useResearchsSuggest';
import { useSearchSuggestNavigation } from '~/composables/useSearchSuggestNavigation';
import { useTwoFieldSuggest } from '~/composables/useTwoFieldSuggest';
import { filtersAppliedMap } from '~/constants';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';

import { SEARCH_PLACEHOLDERS } from '~/components/SearchSuggest/constants';
import SearchSuggestFallback from '~/components/SearchSuggest/SearchSuggestFallback.vue';
import TwoFieldSuggest from '~/components/SearchSuggest/TwoFieldSuggest.vue';

import messages from './Hero.locale';

// Preload da imagem hero com srcset para que o navegador escolha a imagem correta
// baseado na viewport antes de renderizar o <img>
const img = useImage();
const heroImageSrc = '/images/hero/background-hero.webp';

// Dimensões originais da imagem hero (proporção 1.6:1)
const HERO_WIDTH = 1920;
const HERO_HEIGHT = 1200;
const HERO_RATIO = HERO_WIDTH / HERO_HEIGHT;

// Calcula height proporcional para cada width
function getHeroHeight(width: number): number {
	return Math.round(width / HERO_RATIO);
}

// Gera URLs otimizadas para cada breakpoint usando o provider configurado
// Inclui width E height para gerar as mesmas URLs que o NuxtImg
const heroSrcset = [
	`${img(heroImageSrc, { width: 375, height: getHeroHeight(375), format: 'webp', quality: 85 })} 375w`,
	`${img(heroImageSrc, { width: 640, height: getHeroHeight(640), format: 'webp', quality: 85 })} 640w`,
	`${img(heroImageSrc, { width: 768, height: getHeroHeight(768), format: 'webp', quality: 85 })} 768w`,
	`${img(heroImageSrc, { width: 1024, height: getHeroHeight(1024), format: 'webp', quality: 85 })} 1024w`,
	`${img(heroImageSrc, { width: 1280, height: getHeroHeight(1280), format: 'webp', quality: 85 })} 1280w`,
	`${img(heroImageSrc, { width: 1920, height: getHeroHeight(1920), format: 'webp', quality: 85 })} 1920w`
].join(', ');

// Sizes correspondentes aos breakpoints do Tailwind
const heroSizes =
	'(max-width: 639px) 375px, (max-width: 767px) 640px, (max-width: 1023px) 768px, (max-width: 1279px) 1024px, (max-width: 1535px) 1280px, 1920px';

useHead({
	link: [
		{
			rel: 'preload',
			as: 'image',
			href: img(heroImageSrc, {
				width: 375,
				height: getHeroHeight(375),
				format: 'webp',
				quality: 85
			}),
			imagesrcset: heroSrcset,
			imagesizes: heroSizes,
			fetchpriority: 'high'
		}
	]
});

const strings = messages.pt;

const emit = defineEmits<{
	(e: 'filter-applied', params: FilterApplied): void;
}>();

const { updateResearchs } = useResearchsSuggest();

const currentFilterAppliedParams = ref<Partial<FilterApplied>>({});

const isSearchDialogOpen = ref(false);

// INP optimization: defer state changes to reduce input delay
function handleFallbackClick() {
	requestAnimationFrame(() => {
		isSearchDialogOpen.value = true;
	});
}

// Local state to track first field selection and second field enabled state
const firstFieldSelection = ref<SelectAddressEvent | null>(null);
const isSecondFieldEnabled = ref(false);

async function handleExploreClick() {
	// Defer analytics tracking so it doesn't block visual response
	requestAnimationFrame(() => {
		handleExplorePropertiesCTA({
			hasFirstFieldSelection: !!firstFieldSelection.value,
			isSecondFieldEnabled: isSecondFieldEnabled.value,
			firstFieldData: firstFieldSelection.value
		});
	});

	const selection = firstFieldSelection.value;
	if (
		selection &&
		((selection.macroregions && selection.macroregions.length > 0) ||
			(selection.cities && selection.cities.length > 0))
	) {
		// Save history fire-and-forget — must not block navigation
		try {
			const states = (selection as any).states || ['sp'];
			const isMacro =
				Array.isArray(selection.macroregions) &&
				selection.macroregions.length > 0;
			const title = isMacro
				? String(selection.macroregions?.[0] || '')
				: String(selection.cities?.[0] || '');
			const subtitle = String(states?.[0] || '').toUpperCase();

			const suggestion: Suggestion = {
				id: useId(),
				result: { title, subtitle },
				searchParams: {
					regions: selection.regions || [],
					cities: selection.cities || [],
					macroregions: selection.macroregions || [],
					states,
					placeId: null,
					condoSlug: null,
					code: undefined
				},
				searchType: (isMacro ? 'macroregion' : 'city') as any
			};

			updateResearchs(suggestion);
		} catch {
			// noop
		}

		const filters = {
			cities: selection.cities,
			states: selection.states || ['sp'],
			macroregions: selection.macroregions
		} as PropertyFilters;
		const path = PropertyFiltersPresenter.getPathFromFilters(filters);
		return navigateTo(path);
	}

	return navigateTo('/venda/imoveis/sao-paulo-sp-brasil');
}

const groupKeyEval = {
	city: 'city',
	region: 'neighborhood',
	macroregion: 'region'
} as const;

function handleGroupItemClick(event: GroupItemClickEvent) {
	currentFilterAppliedParams.value = {
		...currentFilterAppliedParams.value,
		[MixpanelProps.FILTERS_APPLIED]: groupKeyEval[event.groupKey],
		[MixpanelProps.CURRENT_PAGE]: 'Home',
		[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
		[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
		[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
		[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
		[MixpanelProps.NEIGHBORHOODS_NAME]: event.item.label
	};
}

function handleFirstFieldSelected(event: SelectAddressEvent) {
	firstFieldSelection.value = event;

	const regionName = event.regions?.[0] ?? '';
	const cityName = event.cities?.[0] ?? '';
	const macroregionName = event.macroregions?.[0] ?? '';

	emit('filter-applied', {
		...currentFilterAppliedParams.value,
		[MixpanelProps.CURRENT_PAGE]: 'HomePage',
		[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
		[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
		[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
		[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
		[MixpanelProps.TEXT_SEARCHED]: '',
		[MixpanelProps.REGION_NAME]: macroregionName,
		[MixpanelProps.CITY_NAME]: cityName,
		[MixpanelProps.NEIGHBORHOODS_NAME]: regionName,
		[MixpanelProps.ADDRESS_OR_POINT_OF_INTEREST_NAME]: event.text ?? '',
		[MixpanelProps.FILTERS_APPLIED]:
			filtersAppliedMap[event.searchType ?? 'region'],
		[MixpanelProps.CLICK_FIRST_FIELD]: true,
		[MixpanelProps.CLICK_SECOND_FIELD]: false
	} as FilterApplied);
}

function handleSecondFieldEnabledChange(isEnabled: boolean) {
	isSecondFieldEnabled.value = isEnabled;
}

const { handleExplorePropertiesCTA } = useSearchSuggestNavigation({
	currentPage: 'Home'
});

const groupItemBus = onClientEventBus(
	useEventBus<GroupItemClickEvent>('group-item-click'),
	handleGroupItemClick
);

onUnmounted(() => {
	groupItemBus();
});

const { clearAll } = useTwoFieldSuggest();
onMounted(() => {
	clearAll();
});
</script>

<template>
	<div class="bg-primary">
		<div class="relative h-[90dvh] w-full">
			<div
				class="absolute inset-0 bg-linear-to-b from-black/70 via-black/20 to-black/70"
			/>
			<!-- Hero image otimizada: quality 85 reduz ~30-40% do tamanho sem perda visual perceptível -->
			<!-- Preload é feito manualmente via useHead com imagesrcset/imagesizes para garantir
			     que o navegador faça preload da imagem correta baseado na viewport -->
			<NuxtImg
				:src="heroImageSrc"
				width="1920"
				height="1200"
				sizes="xs:375px sm:640px md:768px lg:1024px xl:1280px 2xl:1920px"
				densities="x1"
				class="aspect-video size-full object-cover object-center"
				format="webp"
				quality="85"
				loading="eager"
				decoding="async"
				fetchpriority="high"
				alt="Hero image - Vista de edifício"
			/>
		</div>
		<div
			class="absolute inset-x-0 top-0 z-30 flex h-[90dvh] w-full flex-col items-center justify-center"
		>
			<LazyContainer class="self-center p-0!">
				<!-- eslint-disable vue/no-v-html -->
				<div class="flex flex-col items-center px-5 lg:px-0">
					<!-- One h1 across breakpoints: the copy no longer differs between
					them, so sizing moves to responsive classes and the page stops
					serving a duplicate heading to crawlers. -->
					<h1
						class="w-full text-center text-[36px] font-normal leading-[125%] tracking-[-0.02em] text-balance text-white lg:text-[48px] lg:leading-[110%] xl:text-[54px]"
						v-html="strings.title.desktop"
					/>
					<div
						class="z-50 mt-4 w-full border-none px-0 md:mt-5 lg:mt-8 lg:w-[600px]"
					>
						<div class="block px-2 md:px-0 lg:hidden">
							<Suspense>
								<template #fallback>
									<SearchSuggestFallback
										is-button
										input-wrapper-classes="w-full border-b border-white-dark-2 bg-white pl-5 py-[10px]"
										:placeholder="SEARCH_PLACEHOLDERS.primary"
										@click="handleFallbackClick"
									/>
								</template>
								<TwoFieldSuggest
									class="relative z-10 w-full max-w-screen-xs-large justify-center border-none"
									first-field-classes="bg-white border-b border-neutral-200"
									second-field-classes="bg-white-dark-2"
									use-dialog
									current-page="HomePage"
									:style-config="{
										inputClasses:
											'h-[50px] md:h-14 text-sm md:text-base placeholder:text-primary-light-1'
									}"
									:behavior-config="{
										enableAnimation: false,
										closeOnClickOutside: true,
										closeOnSelect: true,
										showCloseIcon: false
									}"
									@first-field-selected="handleFirstFieldSelected"
									@second-field-enabled-change="handleSecondFieldEnabledChange"
								/>
							</Suspense>
						</div>

						<!-- Desktop Version: Non-Dialog -->
						<div class="hidden lg:block">
							<Suspense>
								<template #fallback>
									<SearchSuggestFallback
										is-button
										input-wrapper-classes="w-full border-b border-white-dark-2 bg-white pl-5 py-[10px]"
										:placeholder="SEARCH_PLACEHOLDERS.primary"
										@click="handleFallbackClick"
									/>
								</template>
								<TwoFieldSuggest
									class="relative z-10 w-full border-none"
									first-field-classes="bg-white border-b border-neutral-200"
									second-field-classes="bg-white-dark-2"
									current-page="HomePage"
									:style-config="{
										inputClasses:
											'h-[50px] md:h-14 text-sm md:text-base placeholder:text-primary-light-1'
									}"
									:behavior-config="{
										enableAnimation: true,
										closeOnClickOutside: true,
										closeOnSelect: true,
										showCloseIcon: false
									}"
									@first-field-selected="handleFirstFieldSelected"
									@second-field-enabled-change="handleSecondFieldEnabledChange"
								/>
							</Suspense>
						</div>
						<div class="mt-2 w-full px-2 md:px-0">
							<Button
								variant="primary"
								class="flex w-full max-w-screen-xs-large gap-4 place-self-center px-6 py-[11px] lg:max-w-none"
								@click="handleExploreClick"
							>
								<div>
									<IconMaterialSymbolsSearch class="size-6 text-beige" />
								</div>
								<span
									class="whitespace-pre text-[18px] font-medium leading-normal text-white"
								>
									Explorar imóveis
								</span>
							</Button>
						</div>
					</div>
				</div>
			</LazyContainer>
		</div>
	</div>
</template>
